import { createAsyncThunk, createSlice, current, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';
import { apiProcess, apiOkr, apiOrg, apiWorkFollow } from 'app/config/axios-interceptor';
import { IEmployee } from 'app/shared/model/employee.model';
import { IWorkGroup } from 'app/shared/model/work-group.model';
import { BaseResponseEntities, IOption, IQueryParams, serializeAxiosError } from 'app/shared/reducers/reducer.utils';

const initialState = {
  workStatus: [],
  workType: [],
  postType: [],
  qualityType: [],
  quantitative: [],
  krType: [],
  krTrending: [],
  persons: [],
  associates: [],
  checkInTypes: [],
  adjustmentPlanPeriod: [],
  lesserPlanPeriod: [],
  nextLesserPlanPeriod: {},
  frequencies: [],
  totalContribution: [],
  personPositions: [],
  orgsView: [],
  orgsForm: [],
  orgsTree: [],
  workStatusActivities: [],
  priorityLevel: [],
  alignObjKr: [],
  checkInPeriod: [],
  orgCheckIns: [],
  loadingAlign: false,
  checkInStatusActivities: [],
  functionsProcess: [],
  requiredAccept: [],
  ranks: [],
  currentPlanPeriod: {},
};

const apiUrl = 'api/okr/common';
const apiOrgUrl = 'api/org';
const apiContributionUrl = 'api/okr/contribution';
const apiPlanPeriod = 'api/okr/plan-period';
const apiPartyUrl = 'api/okr/party';
const apiAlignUrl = 'api/okr/align-item';
const apiCheckInUrl = 'api/okr/checkin';
const apiFunctionProcess = 'api/functions';
const apiEnumProcess = 'api/enum-selectboxs';
const apiRank = 'api/ranking-evaluation';

// Actions
export const getBoxWorkStatus = createAsyncThunk(
  'common/fetch_work_status_list',
  async ({ searchText, page, size, sort, statusType }: any) => {
    return apiOkr.get<BaseResponseEntities<IWorkGroup>>(`${apiUrl}/select-box-work-status`, {
      params: {
        searchText,
        page,
        size,
        statusType,
        sort: sort && sort,
        // cacheBuster: new Date().getTime(),
      },
    });
  },
  { serializeError: serializeAxiosError },
);

export const getCurrentPlanPeriodOpen = createAsyncThunk(
  'common/fetch_current_plan_period_open',
  async () => {
    return apiOkr.get<any>(`${apiUrl}/active-evaluation-period`);
  },
  { serializeError: serializeAxiosError },
);

export const getSelectBoxFunctionsProcess = createAsyncThunk(
  'common/fetch_all_functions',
  async (type: number) => {
    return apiWorkFollow.get<any[]>(`${apiFunctionProcess}`, { params: { type } });
  },
  { serializeError: serializeAxiosError },
);

export const getSelectBoxRequiredAcceptProcess = createAsyncThunk(
  'common/fetch_all_approvals',
  async () => {
    return apiProcess.get<any[]>(`${apiEnumProcess}/rule-selectboxs`);
  },
  { serializeError: serializeAxiosError },
);

export const getSelectBoxWorkCheckInStatusActivities = createAsyncThunk(
  'common/fetch_all_check_in_status_activities',
  async () => {
    return apiOkr.get<any[]>(`${apiUrl}/activity-checkin-status`);
  },
  { serializeError: serializeAxiosError },
);

export const getSelectBoxWorkStatusActivities = createAsyncThunk(
  'common/fetch_all_status_activities',
  async () => {
    return apiOkr.get<any[]>(`${apiUrl}/activity-work-status`);
  },
  { serializeError: serializeAxiosError },
);

export const getSelectBoxWorkPriorityLevel = createAsyncThunk(
  'common/fetch_all_priority_level',
  async () => {
    return apiOkr.get<any[]>(`${apiUrl}/work-priority-level`);
  },
  { serializeError: serializeAxiosError },
);

export const getOnlyOrgView = createAsyncThunk(
  'common/fetch_all_only_org_view',
  async () => {
    return apiOkr.get<any[]>(`${apiPartyUrl}/org`);
  },
  { serializeError: serializeAxiosError },
);

export const getCheckInPeriod = createAsyncThunk(
  'common/fetch_all_check_in_period',
  async () => {
    return apiOkr.get<any[]>(`${apiUrl}/checkin-period`);
  },
  { serializeError: serializeAxiosError },
);

export const getOrgTree = createAsyncThunk(
  'common/fetch_all_org_tree',
  async () => {
    return apiOkr.get<any[]>(`${apiPartyUrl}/tree`);
  },
  { serializeError: serializeAxiosError },
);

export const getOnlyOrgForm = createAsyncThunk(
  'common/fetch_all_only_org_form',
  async () => {
    return apiOkr.get<any[]>(`${apiPartyUrl}/org`);
  },
  { serializeError: serializeAxiosError },
);

export const getDivergenceEntities = createAsyncThunk(
  'common/fetch_lesser_entity_list',
  async (planPeriodId: string | number) => {
    return apiOkr.get<any[]>(`${apiPlanPeriod}/${planPeriodId}/lesser-plan-period`);
  },
  { serializeError: serializeAxiosError },
);

export const getDivergenceNextPlanPeriod = createAsyncThunk(
  'common/fetch_lesser_next_entity_list',
  async (planPeriodId: string | number) => {
    return apiOkr.get<any[]>(`${apiPlanPeriod}/${planPeriodId}/year-plan-structure`);
  },
  { serializeError: serializeAxiosError },
);

export const getDivergenceNextPlanPeriodTest = createAsyncThunk(
  'common/fetch_lesser_next_entity_list',
  async (planPeriodId: string | number) => {
    return apiOkr.get<any[]>(`${apiPlanPeriod}/${planPeriodId}/mid-year-plan-structure
`);
  },
  { serializeError: serializeAxiosError },
);


export const getBoxWorkType = createAsyncThunk(
  'common/fetch_work_type_list',
  async () => {
    return apiOkr.get<IWorkGroup[]>(`${apiUrl}/select-box-work-type`);
  },
  { serializeError: serializeAxiosError },
);

export const getBoxPostType = createAsyncThunk(
  'common/fetch_post_type_list',
  async ({ searchText, page, size, sort, filters }: IQueryParams) => {
    return apiOrg.get<BaseResponseEntities<any>>(`${apiOrgUrl}/common/select-box-post-type`, {
      params: {
        searchText,
        page,
        size,
        filters: filters && JSON.stringify(filters),
        sort: sort && sort,
        // cacheBuster: new Date().getTime(),
      },
    });
  },
  { serializeError: serializeAxiosError },
);

export const getPersons = createAsyncThunk(
  'common/fetch_person_list',
  async ({ searchText, page, size, sort, filters }: IQueryParams) => {
    return apiOrg.get<BaseResponseEntities<any>>(`${apiOrgUrl}/person`, {
      params: {
        searchText,
        pageNum: page,
        pageSize: 9999,
        filters: filters && JSON.stringify(filters),
        sort: sort && sort,
      },
    });
  },
  { serializeError: serializeAxiosError },
);

export const getQualityEvaluationTypes = createAsyncThunk(
  'common/fetch_quality_evaluation_list',
  async () => {
    return apiOkr.get<BaseResponseEntities<any>>(`${apiUrl}/select-box-evaluation-method`);
  },
  { serializeError: serializeAxiosError },
);

export const getKrTrendingQuantitativeTypes = createAsyncThunk(
  'common/fetch_kr_trending_quantitative_list',
  async () => {
    return apiOkr.get<IOption[]>(`${apiUrl}/select-box-trending-quantitative-type`);
  },
  { serializeError: serializeAxiosError },
);

export const getQuantitativeEvaluationTypes = createAsyncThunk(
  'common/fetch_quantitative_evaluation_list',
  async () => {
    return apiOkr.get<IOption[]>(`${apiUrl}/select-box-evaluation-quantitative-type`);
  },
  { serializeError: serializeAxiosError },
);

export const getKrTypes = createAsyncThunk(
  'common/fetch_kr_types_list',
  async () => {
    return apiOkr.get<BaseResponseEntities<any>>(`${apiUrl}/select-box-kr-type`);
  },
  { serializeError: serializeAxiosError },
);

export const getAssociates = createAsyncThunk(
  'common/fetch_associates_list',
  async () => {
    return apiOkr.get<any[]>(`${apiUrl}/select-box-associate-item`);
  },
  { serializeError: serializeAxiosError },
);
interface IQueryParamsAlign extends IQueryParams {
  planPeriodId?: string;
  okrVersion?: number;
  partyId?: string;
}
export const getAlignObjectiveKr = createAsyncThunk(
  'common/fetch_align_objective_kr_list',
  async ({ searchText, page, size, sort, planPeriodId, okrVersion, partyId }: IQueryParamsAlign, { rejectWithValue }) => {
    return apiOkr
      .get<any[]>(`${apiAlignUrl}/align-item-for-activity`, {
        params: {
          pageNum: page,
          pageSize: size,
          searchText,
          sort: sort && sort,
          planPeriodId,
          okrVersion,
          partyId,
        },
      })
      .catch(error => {
        return rejectWithValue(error);
      });
  },
  { serializeError: serializeAxiosError },
);

export const getAdjustPlanPeriod = createAsyncThunk(
  'common/fetch_adjust_plan_period_list',
  async ({ planPeriodId }: { planPeriodId: any }) => {
    return apiOkr.get<any[]>(`${apiUrl}/select-box-adjustment-period`, { params: { planPeriodId } });
  },
  { serializeError: serializeAxiosError },
);

export const getCheckInType = createAsyncThunk(
  'common/fetch_list_checkIn_type',
  async () => {
    return apiOkr.get<any[]>(`${apiUrl}/select-box-checkin-detail-by`);
  },
  { serializeError: serializeAxiosError },
);

export const getFrequencyType = createAsyncThunk(
  'common/fetch_list_frequency_type',
  async (id?: null | string) => {
    return apiOkr.get<any[]>(`${apiUrl}/select-box-frequency`, { params: { planPeriodId: id } });
  },
  { serializeError: serializeAxiosError },
);

export const getOnlyPersonInOrg = createAsyncThunk(
  'common/fetch_all_person_in_org',
  async () => {
    return apiOkr.get<any[]>(`${apiPartyUrl}/person-in-org`);
  },
  { serializeError: serializeAxiosError },
);

type TotalContribution = {
  planPeriodId: string;
  contributionGroupBy: number;
  adjustmentPeriod: number;
  accountableId: string;
};

export const viewTotalCOntribution = createAsyncThunk(
  'common/fetch_total_contribution',
  async ({ adjustmentPeriod, contributionGroupBy, planPeriodId, accountableId }: TotalContribution) => {
    return apiOkr.get<any[]>(`${apiContributionUrl}/group-contribution`, {
      params: {
        planPeriodId,
        adjustmentPeriod,
        contributionGroupBy,
        accountableId,
      },
    });
  },
  { serializeError: serializeAxiosError },
);

type IOrgCheckIn = {
  searchText?: string;
  accountableId: string;
  checkinStatus?: number;
};

export const getListOrgCheckIn = createAsyncThunk(
  'checkIn/fetch_all_org_checkin',
  async ({ accountableId, searchText, checkinStatus }: IOrgCheckIn) => {
    return apiOkr.get<any>(`${apiCheckInUrl}/checkin-list`, {
      params: {
        accountableId,
        searchText,
        checkinStatus,
      },
    });
  },
  { serializeError: serializeAxiosError },
);

export const getRanks = createAsyncThunk(
  'common/fetch_all_rank',
  async ({ partyId, year }: any) => {
    return apiProcess.get<any[]>(`${apiRank}/select-box`, { params: { partyId, year } });
  },
  { serializeError: serializeAxiosError },
);

export const getRankByPoint = createAsyncThunk(
  'common/fetch_rank_by_point',
  async ({ partyId, year, point }: any, { signal }) => {
    const controller = new AbortController();
    signal.addEventListener('abort', () => controller.abort()); // Liên kết AbortController với signal của thunk
    return apiProcess.get<any[]>(`${apiRank}/by-point`, { params: { partyId, year, point }, signal: controller.signal });
  },
  { serializeError: serializeAxiosError },
);

// slice

export const CommonSlice = createSlice({
  name: 'common',
  initialState,
  reducers: {
    resetCommon(state) {
      return initialState;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getCurrentPlanPeriodOpen.fulfilled, (state, action) => {
        const { data, headers } = action.payload;
        state.currentPlanPeriod = data;
      })
      .addCase(getRanks.fulfilled, (state, action) => {
        const { data, headers } = action.payload;
        state.ranks = data;
      })
      .addCase(getSelectBoxRequiredAcceptProcess.fulfilled, (state, action) => {
        const { data, headers } = action.payload;
        state.requiredAccept = data;
      })
      .addCase(getSelectBoxFunctionsProcess.fulfilled, (state, action) => {
        const { data, headers } = action.payload;
        state.functionsProcess = data;
      })
      .addCase(getOrgTree.fulfilled, (state, action) => {
        const { data, headers } = action.payload;
        state.orgsTree = data;
      })
      .addCase(getSelectBoxWorkPriorityLevel.fulfilled, (state, action) => {
        const { data, headers } = action.payload;
        state.priorityLevel = data;
      })
      .addCase(getSelectBoxWorkCheckInStatusActivities.fulfilled, (state, action) => {
        const { data, headers } = action.payload;
        state.checkInStatusActivities = data;
      })
      .addCase(getSelectBoxWorkStatusActivities.fulfilled, (state, action) => {
        const { data, headers } = action.payload;
        state.workStatusActivities = data;
      })
      .addCase(getOnlyOrgView.fulfilled, (state, action) => {
        const { data, headers } = action.payload;
        state.orgsView = data;
      })
      .addCase(getListOrgCheckIn.fulfilled, (state, action) => {
        const { data, headers } = action.payload;
        state.orgCheckIns = data;
      })
      .addCase(getCheckInPeriod.fulfilled, (state, action) => {
        const { data, headers } = action.payload;
        state.checkInPeriod = data;
      })
      .addCase(getOnlyOrgForm.fulfilled, (state, action) => {
        const { data, headers } = action.payload;
        state.orgsForm = data;
      })
      .addCase(getOnlyPersonInOrg.fulfilled, (state, action) => {
        const { data, headers } = action.payload;
        state.personPositions = data;
      })
      .addCase(viewTotalCOntribution.fulfilled, (state, action) => {
        const { data, headers } = action.payload;
        state.totalContribution = data;
      })
      .addCase(getDivergenceEntities.fulfilled, (state, action) => {
        const { data, headers } = action.payload;
        state.lesserPlanPeriod = data;
      })
      .addCase(getDivergenceNextPlanPeriod.fulfilled, (state, action) => {
        const { data, headers } = action.payload;
        state.nextLesserPlanPeriod = data;
      })
      .addCase(getFrequencyType.fulfilled, (state, action) => {
        const { data, headers } = action.payload;
        state.frequencies = data;
      })
      .addCase(getCheckInType.fulfilled, (state, action) => {
        const { data, headers } = action.payload;
        state.checkInTypes = data;
      })
      .addCase(getAdjustPlanPeriod.fulfilled, (state, action) => {
        const { data, headers } = action.payload;
        state.adjustmentPlanPeriod = data;
      })
      .addCase(getAlignObjectiveKr.fulfilled, (state, action) => {
        const { data, headers } = action.payload;
        state.alignObjKr = data;
        state.loadingAlign = false;
      })
      .addCase(getAssociates.fulfilled, (state, action) => {
        const { data, headers } = action.payload;
        state.associates = data;
      })
      .addCase(getKrTrendingQuantitativeTypes.fulfilled, (state, action) => {
        const { data, headers } = action.payload;
        state.krTrending = data;
      })
      .addCase(getQuantitativeEvaluationTypes.fulfilled, (state, action) => {
        const { data, headers } = action.payload;
        state.quantitative = data;
      })
      .addCase(getKrTypes.fulfilled, (state, action) => {
        const { data, headers } = action.payload;
        state.krType = data.items as any;
      })
      .addCase(getQualityEvaluationTypes.fulfilled, (state, action) => {
        const { data, headers } = action.payload;
        state.qualityType = data.items as any;
      })
      .addCase(getBoxWorkStatus.fulfilled, (state, action) => {
        const { data, headers } = action.payload;
        state.workStatus = data.items as any;
      })
      .addCase(getBoxWorkType.fulfilled, (state, action) => {
        const { data, headers } = action.payload;
        state.workType = data as any;
      })
      .addCase(getBoxPostType.fulfilled, (state, action) => {
        const { data, headers } = action.payload;
        state.postType = data.items as any;
      })
      .addCase(getPersons.fulfilled, (state, action) => {
        const { data, headers } = action.payload;
        state.persons = data.items as any;
      })
      .addMatcher(isPending(getRanks), state => {
        state.ranks = [];
      })
      .addMatcher(isPending(getCurrentPlanPeriodOpen), state => {
        state.currentPlanPeriod = {};
      })
      .addMatcher(isPending(getSelectBoxRequiredAcceptProcess), state => {
        state.requiredAccept = [];
      })
      .addMatcher(isPending(getSelectBoxFunctionsProcess), state => {
        state.functionsProcess = [];
      })
      .addMatcher(isPending(getOrgTree), state => {
        state.krTrending = [];
      })
      .addMatcher(isPending(getSelectBoxWorkPriorityLevel), state => {
        state.priorityLevel = [];
      })
      .addMatcher(isPending(getSelectBoxWorkCheckInStatusActivities), state => {
        state.checkInStatusActivities = [];
      })
      .addMatcher(isPending(getSelectBoxWorkStatusActivities), state => {
        state.workStatusActivities = [];
      })
      .addMatcher(isPending(getCheckInPeriod), state => {
        state.checkInPeriod = [];
      })
      .addMatcher(isPending(getListOrgCheckIn), state => {
        state.orgCheckIns = [];
      })
      .addMatcher(isPending(getOnlyOrgView), state => {
        state.orgsView = [];
      })
      .addMatcher(isPending(getOnlyOrgForm), state => {
        state.orgsForm = [];
      })
      .addMatcher(isPending(getOnlyPersonInOrg), state => {
        state.personPositions = [];
      })
      .addMatcher(isPending(viewTotalCOntribution), state => {
        state.totalContribution = [];
      })
      .addMatcher(isPending(getDivergenceNextPlanPeriod), state => {
        state.nextLesserPlanPeriod = [];
      })
      .addMatcher(isPending(getDivergenceEntities), state => {
        state.lesserPlanPeriod = [];
      })
      .addMatcher(isPending(getFrequencyType), state => {
        state.frequencies = [];
      })
      .addMatcher(isPending(getCheckInType), state => {
        state.checkInTypes = [];
      })
      .addMatcher(isPending(getAdjustPlanPeriod), state => {
        state.adjustmentPlanPeriod = [];
      })
      .addMatcher(isPending(getAssociates), state => {
        state.associates = [];
      })
      .addMatcher(isPending(getAlignObjectiveKr), state => {
        state.alignObjKr = [];
        state.loadingAlign = true;
      })
      .addMatcher(isRejected(getAlignObjectiveKr), state => {
        state.loadingAlign = false;
      })
      .addMatcher(isPending(getKrTrendingQuantitativeTypes), state => {
        state.krTrending = [];
      })
      .addMatcher(isPending(getQuantitativeEvaluationTypes), state => {
        state.quantitative = [];
      })
      .addMatcher(isPending(getKrTypes), state => {
        state.krType = [];
      })
      .addMatcher(isPending(getQualityEvaluationTypes), state => {
        state.qualityType = [];
      })
      .addMatcher(isPending(getPersons), state => {
        state.persons = [];
      })
      .addMatcher(isPending(getBoxWorkStatus), state => {
        state.workStatus = [];
      })
      .addMatcher(isPending(getBoxPostType), state => {
        state.postType = [];
      })
      .addMatcher(isPending(getBoxWorkType), state => {
        state.workType = [];
      });
  },
});
export const { resetCommon } = CommonSlice.actions;
// Reducer
export default CommonSlice.reducer;
